// Dashboard-only boundary. The shared findings service must still return review findings.
export const VERIFIED_ANOMALY_FILTER_KEYS = Object.freeze([
  'category',
  'type',
  'severity',
  'scan',
  'plant',
  'equipmentTags',
  'pipelineTags',
  'funcLocation',
  'area',
  'system',
  'subSystem',
  'structuralType',
]);

export function normalizeVerifiedAnomalyFilters(filters = {}) {
  const source = filters && typeof filters === 'object' ? filters : {};
  return Object.fromEntries(
    VERIFIED_ANOMALY_FILTER_KEYS.map((key) => [
      key,
      Array.isArray(source[key]) ? [...source[key]] : [],
    ]),
  );
}

export function verifiedAnomalyQuery(options = {}, facilityId = options.facilityId) {
  return {
    ...options,
    facilityId,
    sortBy: Array.isArray(options.sortBy) ? options.sortBy[0] || null : options.sortBy || null,
    sortDesc: Boolean(Array.isArray(options.sortDesc) ? options.sortDesc[0] : options.sortDesc),
    filters: { ...normalizeVerifiedAnomalyFilters(options.filters), approvalStatus: ['Accepted'] },
  };
}

export function acceptedAnomalies(entries, facilityId) {
  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry?.approvalStatus === 'Accepted' &&
      (!facilityId || !entry.facilityId || entry.facilityId === facilityId))
    .map((entry) => ({
      ...entry,
      ...(Array.isArray(entry.children) ? { children: acceptedAnomalies(entry.children, facilityId) } : {}),
    }));
}

export function verifiedAnomalyPage(data, facilityId) {
  if (!Array.isArray(data?.items) || !Number.isInteger(data.total) || data.total < data.items.length) {
    throw new Error('The verified anomaly response is invalid. Please retry.');
  }
  if (data.items.some((entry) => entry?.approvalStatus !== 'Accepted')) {
    throw new Error('The server did not return an accepted-only result. Verified anomalies cannot be refreshed.');
  }
  if (data.items.some((entry) => entry.facilityId && entry.facilityId !== facilityId)) {
    throw new Error('The server returned anomalies outside the selected facility.');
  }
  return { items: acceptedAnomalies(data.items, facilityId), total: data.total };
}

/** Findings loaded per AVA Inspect group; a tag with more shows the first ones (newest first). */
export const AVA_GROUP_PAGE_SIZE = 500;

/**
 * A page of AVA Inspect groups from `avaInspectGroups`: [{ avaInspectId, avaInspectName, avaInspectTitle,
 * masterTag, findingCount, assessment }]. Full scan findings are not grouped, so they never appear.
 */
export function avaInspectGroupPage(data) {
  if (!Array.isArray(data?.items) || !Number.isInteger(data.total) || data.total < data.items.length) {
    throw new Error('The AVA Inspect group response is invalid. Please retry.');
  }
  if (data.items.some((group) => !group?.avaInspectId || !Number.isInteger(group.findingCount))) {
    throw new Error('The server returned an AVA Inspect group without a tag or a finding count.');
  }
  return { items: data.items, total: data.total };
}

/** The query of one group's findings: the page query, scoped to the tag, first page of its rows. */
export function avaInspectGroupQuery(options = {}, facilityId, avaInspectId) {
  return { ...verifiedAnomalyQuery(options, facilityId), avaInspectId, page: 1, itemsPerPage: AVA_GROUP_PAGE_SIZE };
}

/** `entries` with the rows of one group replaced by `items` (other groups' rows keep their order). */
export function replaceGroupEntries(entries, avaInspectId, items) {
  const others = (Array.isArray(entries) ? entries : []).filter((entry) => entry.avaInspectId !== avaInspectId);
  return [...others, ...(Array.isArray(items) ? items : []).map((item) => ({ ...item, avaInspectId }))];
}
